import { InlineFragmentSymbol } from "./typeUtils";
export const createClient = ({ onRequest, onSubscribe, includeTypeNames, cacheQueries, }) => {
    return {
        query: (query, ...params) => query((queryString, variables) => onRequest(queryString, variables, params[1]), params[0], { includeTypeNames, cacheQueries }),
        subscribe: (query, onMessage, ...variables) => query(onSubscribe, onMessage, variables[0], {
            includeTypeNames,
            cacheQueries,
            // eslint-disable-next-line
        }),
    };
};
export const createQueryUtils = (enums, argumentsByField) => {
    function createQueryArgumentsString(fieldKey, args, detectedVariableTypes, variables) {
        return `(${Object.keys(args)
            .reduce((aggr, key) => {
            const val = args[key];
            const isVariable = typeof val === "string" && val[0] === "$";
            const isEnum = typeof val === "string" && enums.includes(val);
            // Arguments can be undefined, we do not include the arg in that case
            if (val === undefined) {
                return aggr;
            }
            if (isVariable) {
                detectedVariableTypes[val] = argumentsByField[fieldKey][key];
            }
            // We do not add the argument if the variable is undefined
            return isVariable &&
                variables &&
                variables[val.substring(1)] === undefined
                ? aggr
                : aggr.concat(`${key}: ${isVariable || isEnum
                    ? val
                    : typeof val === "string"
                        ? `"${val}"`
                        : val}`);
        }, [])
            .join(", ")})`;
    }
    function isObjectQueryDefinition(queryDefinition) {
        return Array.isArray(queryDefinition) && queryDefinition.length === 2;
    }
    function isFieldQueryDefinition(queryDefinition) {
        return Array.isArray(queryDefinition) && queryDefinition.length === 1;
    }
    function createQueryBodyString(queryDefinitions, detectedVariableTypes, includeTypeNames, variables, level = 1) {
        let string = " {\n";
        if (level > 1 && includeTypeNames) {
            string +=
                "  ".repeat(level) +
                    "__typename\n" +
                    ("id" in queryDefinitions ? "" : "  ".repeat(level) + "id\n");
        }
        // eslint-disable-next-line
        // @ts-ignore
        const topLevelUnionField = queryDefinitions[InlineFragmentSymbol];
        if (topLevelUnionField) {
            for (const key in queryDefinitions) {
                string += "  ".repeat(level) + `... on ${key}`;
                string += createQueryBodyString(
                // eslint-disable-next-line
                // @ts-ignore
                queryDefinitions[key], detectedVariableTypes, includeTypeNames, variables, level + 1);
            }
            string += "  ".repeat(level - 1) + "}\n";
            return string;
        }
        for (let field in queryDefinitions) {
            const value = queryDefinitions[field];
            if (value === false || value === undefined) {
                continue;
            }
            const aliasTuple = field.split(":");
            const aliasPrefix = aliasTuple[1] ? ":" + aliasTuple[1] : "";
            field = aliasTuple[0];
            string += "  ".repeat(level) + field;
            if (value === undefined) {
                // This can not really happen, but due to typing it needs to be handled
                throw new Error("Got an undefined, that should not be possible!");
            }
            if (value === true) {
                string += "\n";
            }
            else if (isFieldQueryDefinition(value)) {
                string +=
                    createQueryArgumentsString(aliasTuple[1] || aliasTuple[0], value[0], detectedVariableTypes, variables) + "\n";
            }
            else if (isObjectQueryDefinition(value)) {
                string +=
                    aliasPrefix +
                        createQueryArgumentsString(aliasTuple[1] || aliasTuple[0], value[0], detectedVariableTypes, variables) +
                        createQueryBodyString(value[1], detectedVariableTypes, includeTypeNames, variables, level + 1);
            }
            else {
                // eslint-disable-next-line
                // @ts-ignore
                const unionFieldType = value[InlineFragmentSymbol];
                if (unionFieldType) {
                    string += aliasPrefix + " {\n";
                    for (const key in value) {
                        string += "  ".repeat(level + 1) + `... on ${key}`;
                        string += createQueryBodyString(
                        // eslint-disable-next-line
                        // @ts-ignore
                        value[key], detectedVariableTypes, includeTypeNames, variables, level + 2);
                    }
                    string += "}\n";
                }
                else {
                    string +=
                        aliasPrefix +
                            createQueryBodyString(value, detectedVariableTypes, includeTypeNames, variables, level + 1);
                }
            }
        }
        string += "  ".repeat(level - 1) + "}\n";
        return string;
    }
    function createVariablesString(variables, detectedVariableTypes) {
        return Object.keys(variables)
            .reduce((aggr, key) => {
            // The variable might be undefined, where we just skip it
            if (variables[key] === undefined) {
                return aggr;
            }
            const varKey = `$${key}`;
            const gqlType = detectedVariableTypes[varKey];
            if (!gqlType) {
                throw new Error(`Unable to detect variable type for key ${key}`);
            }
            return aggr.concat(`${varKey}: ${gqlType.isList ? "[" : ""}${gqlType.type}${gqlType.isNonNull ? "!" : ""}${gqlType.isList ? "]" : ""}`);
        }, [])
            .join(", ");
    }
    function createQueryStringFactory(type) {
        return function createQueryString(name, query, variables, includeTypeNames) {
            const detectedVariableTypes = {};
            const queryBodyString = createQueryBodyString(query, detectedVariableTypes, includeTypeNames, variables);
            return `${type} ${name} ${variables
                ? `(${createVariablesString(variables, detectedVariableTypes)})`
                : ""}${queryBodyString}`;
        };
    }
    const createQueryString = createQueryStringFactory("query");
    const createMutationString = createQueryStringFactory("mutation");
    const createSubscriptionString = createQueryStringFactory("subscription");
    return {
        createMutation: (name, cb) => (request, variables, { includeTypeNames }) => {
            const query = typeof cb === "function"
                ? cb(Object.keys(variables || {}).reduce((aggr, key) => {
                    aggr[key] = "$" + key;
                    return aggr;
                }, {}))
                : // eslint-disable-next-line
                    cb;
            return request(createMutationString(name, query, variables ? variables : undefined, includeTypeNames), variables
                ? Object.keys(variables).reduce((aggr, key) => {
                    aggr[key] = variables[key];
                    return aggr;
                }, {})
                : {});
        },
        createQuery: (name, cb) => (request, variables, { includeTypeNames }) => {
            const query = typeof cb === "function"
                ? // eslint-disable-next-line
                    cb(Object.keys(variables || {}).reduce((aggr, key) => {
                        aggr[key] = "$" + key;
                        return aggr;
                    }, {}))
                : // eslint-disable-next-line
                    cb;
            return request(createQueryString(name, query, variables ? variables : undefined, includeTypeNames), variables
                ? Object.keys(variables).reduce((aggr, key) => {
                    aggr[key] = variables[key];
                    return aggr;
                }, {})
                : {});
        },
        createSubscription: (name, cb) => (subscribe, onMessage, variables, { includeTypeNames }) => {
            const query = typeof cb === "function"
                ? cb(Object.keys(variables || {}).reduce((aggr, key) => {
                    aggr[key] = "$" + key;
                    return aggr;
                }, {}))
                : // eslint-disable-next-line
                    cb;
            return subscribe(createSubscriptionString(name, query, variables ? variables : undefined, includeTypeNames), 
            // eslint-disable-next-line
            onMessage, variables
                ? Object.keys(variables).reduce((aggr, key) => {
                    aggr[key] = variables[key];
                    return aggr;
                }, {})
                : {});
        },
    };
};
//# sourceMappingURL=utils.js.map